/**
 * "Send to annotation" from an experiment's results: the ticked results go
 * into one of my queues (or one shared with me), or a new queue made here with
 * its scores and reviewers. Results already in the queue are skipped. Then a
 * link opens the queue.
 */

import { useState } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/overlay";
import { QueryState, errorText } from "@/components/ui/states";
import { plural } from "@/lib/format";
import { useUiStore } from "@/stores/ui-store";
import { useAddItems, useCreateQueue, useQueues } from "./hooks";
import { QueueFields, draftReady, emptyDraft, toCreate } from "./queue-fields";

const NEW = "__new__";

interface Sent {
  id: string;
  name: string;
  added: number;
}

function SentDialog({ sent, count, onClose }: { sent: Sent; count: number; onClose: () => void }) {
  const already = count - sent.added;
  return (
    <Dialog
      title="Sent to annotation"
      onClose={onClose}
      width={480}
      footer={
        <>
          <span className="flex-1" />
          <Button onClick={onClose}>Done</Button>
        </>
      }
    >
      <p className="text-[13px]">
        {plural(sent.added, "item")} added to <b>{sent.name}</b>
        {already > 0 && ` (${already} ${already === 1 ? "was" : "were"} already in it)`}. Reviewers see them
        in the queue.
      </p>
      <Link className="link text-[13px]" to={`/annotation/${sent.id}`}>
        Open {sent.name}
      </Link>
    </Dialog>
  );
}

export function SendToQueueDialog({
  experiment,
  resultIds,
  onClose,
  onSent,
}: {
  experiment: { id: string; name: string };
  resultIds: string[];
  onClose: () => void;
  onSent: () => void;
}) {
  const queues = useQueues();
  const [picked, setPicked] = useState<string | null>(null);
  const [draft, setDraft] = useState(() => emptyDraft(`Review · ${experiment.name}`));
  const [sent, setSent] = useState<Sent | null>(null);
  const create = useCreateQueue();
  const add = useAddItems();
  const notify = useUiStore((s) => s.notify);
  const target = picked ?? queues.data?.[0]?.id ?? NEW;
  const busy = create.isPending || add.isPending;

  const fail = (e: unknown) => notify(errorText(e, "Could not send the items."), "error");
  const addTo = (queue: { id: string; name: string }) =>
    add.mutate(
      { queueId: queue.id, experiment_id: experiment.id, item_result_ids: resultIds },
      { onSuccess: (r) => (setSent({ ...queue, added: r.added }), onSent()), onError: fail },
    );
  const send = () => {
    if (target !== NEW) {
      addTo({ id: target, name: queues.data?.find((q) => q.id === target)?.name ?? "" });
      return;
    }
    create.mutate(toCreate(draft), { onSuccess: addTo, onError: fail });
  };

  if (sent) return <SentDialog sent={sent} count={resultIds.length} onClose={onClose} />;

  const options = [
    ...(queues.data ?? []).map((q) => ({
      id: q.id,
      label: q.name,
      sub: `${plural(q.total, "item")} · asks ${q.scores.map((s) => s.name).join(", ")}`,
    })),
    { id: NEW, label: "New queue", sub: "Pick its scores and who reviews" },
  ];
  return (
    <Dialog
      title="Send to annotation"
      subtitle="People score these answers one by one. Their scores sit next to the judges' scores."
      onClose={onClose}
      width={600}
      footer={
        <>
          <span className="flex-1" />
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={busy || (target === NEW && !draftReady(draft))} onClick={send}>
            Send {plural(resultIds.length, "item")}
          </Button>
        </>
      }
    >
      <QueryState query={queues} what="queues">
        <fieldset className="flex flex-col gap-1.5">
          <legend className="pb-1.5 text-[13px] font-semibold">Queue</legend>
          {options.map((o) => (
            <label
              key={o.id}
              className={`flex cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2 ${
                target === o.id ? "border-primary bg-selected" : "border-line hover:bg-hover"
              }`}
            >
              <input
                type="radio"
                name="queue"
                className="mt-1 accent-ink"
                checked={target === o.id}
                onChange={() => setPicked(o.id)}
              />
              <span className="flex min-w-0 flex-col">
                <span className="text-[13px] font-medium">{o.label}</span>
                <span className="truncate text-xs text-muted">{o.sub}</span>
              </span>
            </label>
          ))}
        </fieldset>
      </QueryState>
      {target === NEW && (
        <QueueFields
          draft={draft}
          withDescription={false}
          onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}
        />
      )}
    </Dialog>
  );
}
